import { useEffect, useState } from "react";
import Button from "react-bootstrap/Button";
import Modal from "react-bootstrap/Modal";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import { useAssignReservationMutation } from "../../../store/api/school/ReservationSlice.js";
import {
  useGetAllActiveDriversQuery,
  useGetVendorDetailsQuery,
} from "../../../store/api/school/VendorSlice.js";
import { useGetAllRoutesSchedulesQuery } from "../../../store/api/vendor/routesSchedulesSlice.js";
import { useGetAllVehiclesQuery } from "../../../store/api/vendor/vehiclesSlice.js";
import {
  capitalizeFirstLetter,
  capitalizeString,
  convertTime,
  formatDateToLocal,
} from "../../../utils/dateAndTimeConversion.js";
import { connectSocketWithUserId, socket } from "../../../utils/socket.js";
import { getScheduleStatusInfo } from "../Driver/Schedules.js";

const initialState = {
  driver_id: "",
  vehicle_id: "",
  route_id: "",
  driver_schedule_id: "",
};

const BusingAssignDriver = ({ bookingId, role, isTransfer = false }) => {
  const { t } = useTranslation();
  const [show, setShow] = useState(false);
  const { user: authUser } = useSelector((state) => state.UserReducer);
  const [errors, setErrors] = useState({});
  const [formData, setFormData] = useState(initialState);
  const [drivers, setDrivers] = useState([]);
  const [driverSchedule, setDriverSchedule] = useState([]);
  const [availableSchedules, setAvailableSchedules] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [routes, setRoutes] = useState([]);

  // Get active drivers
  const { data, isSuccess } = useGetAllActiveDriversQuery(undefined, {
    skip: !show,
    refetchOnMountOrArgChange: true, // ✅ Har baar modal open ho, fresh API hit hogi
  });

  // Get Vehicles
  const { data: VehiclesData, isSuccess: isSuccessVehicles } =
    useGetVendorDetailsQuery({});

  // Get Route Schedules
  const {
    data: routesData,
    isSuccess: routesIsSuccess,
    isLoading: routesIsLoading,
    isFetching: routesIsFetching,
  } = useGetAllRoutesSchedulesQuery({});

  // Create New Driver query
  const [assignReservation, { isLoading }] = useAssignReservationMutation();

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    // Reset driver_schedule_id when driver changes
    if (name === "driver_id") {
      setFormData((prev) => ({ ...prev, driver_schedule_id: "" }));
    }

    setErrors((prev) => ({
      ...prev,
      [name]: value ? "" : `${t("errors." + name)} ${t("errors.required")}`,
    }));
  };

  useEffect(() => {
    if (isSuccess && data?.data) {
      setDrivers(data.data.drivers);
    }
  }, [data, isSuccess]);

  useEffect(() => {
    if (isSuccessVehicles && VehiclesData?.data) {
      setVehicles(VehiclesData?.data?.vehicles);
    }
  }, [VehiclesData, isSuccessVehicles]);

  useEffect(() => {
    if (routesIsSuccess && routesData?.data) {
      setRoutes(routesData.data.routes);
    }
  }, [routesData, routesIsSuccess]);

  useEffect(() => {
    if (formData.driver_id) {
      const selectedDriver = drivers.find(
        (d) => d?.driver_details?.driver_id === parseInt(formData.driver_id),
      );
      if (selectedDriver?.driver_details?.schedules) {
        setDriverSchedule(selectedDriver.driver_details.schedules);

        // Filter schedules: only show schedules with status "in_progress"
        const filteredSchedules =
          selectedDriver.driver_details.schedules.filter(
            (schedule) => schedule.status === "available",
          );
        setAvailableSchedules(filteredSchedules);
      } else {
        setDriverSchedule([]);
        setAvailableSchedules([]);
      }
    } else {
      setDriverSchedule([]);
      setAvailableSchedules([]);
    }
  }, [formData.driver_id, drivers]);

  const validateForm = () => {
    const newErrors = {};
    Object.entries(formData).forEach(([key, value]) => {
      if (key === "vehicle_id" && (role === "school" || !isTransfer)) return;

      if (!value)
        newErrors[key] = `${t("errors." + key)} ${t("errors.required")}`;
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  useEffect(() => {
    if (authUser && (authUser.id || authUser._id)) {
      connectSocketWithUserId(authUser.id || authUser._id);
      const onConnect = () => {
        console.log("Socket ID:", socket.id, "Connected:", socket.connected);
      };

      socket.on("connect", onConnect);

      // If already connected (hot reload), call handler immediately
      if (socket.connected) {
        onConnect();
      }
      // Cleanup: remove connect listener and any others
      return () => {
        socket.off("connect", onConnect);
      };
    }
  }, [socket, authUser]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    toast.dismiss();

    const { vehicle_id, ...rest } = formData;
    const payload = role === "school" ? rest : formData;

    try {
      let response = await assignReservation({
        data: payload,
        id: bookingId,
        endPoint: "assign",
      }).unwrap();
      if (response.statusCode === 200 || response.statusCode === 201) {
        toast.success(response.message);
        setShow(false);

        if (socket.connected) {
          socket.emit("vehicle-driver_notification", {
            driver_name: response?.data?.driver_name,
            license_number: response?.data?.license_number,
            school_id: response?.data?.school_id,
          });
        }
      }
    } catch (err) {
      const mesg = err.data?.message || "Something went wrong";
      toast.error(mesg);
    }
  };

  const shiftTypeLabel = (type) => {
    if (type === "morning_afternoon") return "Morning & Afternoon";
    if (type === "morning") return "Morning";
    if (type === "afternoon") return "Afternoon";
    return capitalizeString(type);
  };

  const formatScheduleOption = (schedule) => {
    const shift = shiftTypeLabel(schedule.shift_type);
    const startDate = formatDateToLocal(schedule.start_date);
    const endDate = formatDateToLocal(schedule.end_date);
    const startTime = convertTime(schedule.start_time);
    const endTime = convertTime(schedule.end_time);
    if (
      schedule.shift_type === "morning_afternoon" &&
      schedule.return_start_time &&
      schedule.return_end_time
    ) {
      const retStart = convertTime(schedule.return_start_time);
      const retEnd = convertTime(schedule.return_end_time);
      return `${shift} | ${startDate} – ${endDate} | Morning: ${startTime}–${endTime} | Afternoon: ${retStart}–${retEnd}`;
    }
    return `${shift} | ${startDate} ${startTime} – ${endDate} ${endTime}`;
  };

  // Helper function to format recurrence days
  const formatRecurrenceDays = (daysString) => {
    try {
      const days = JSON.parse(daysString);
      return days
        .map((day) => day.charAt(0).toUpperCase() + day.slice(1))
        .join(", ");
    } catch {
      return daysString;
    }
  };

  return (
    <>
      <Button onClick={() => setShow(true)}>{t("btns.assign")}</Button>
      <Modal
        show={show}
        onHide={() => setShow(false)}
        size="lg"
        centered
        backdrop="static"
        className="assignModel"
      >
        <Modal.Header closeButton className="border-0">
          <Modal.Title className="h5">
            {t("assign-resources-to-booking")} #{bookingId}
          </Modal.Title>
        </Modal.Header>

        <Modal.Body
          style={{
            maxHeight: "calc(90vh - 120px)",
            overflowY: "auto",
          }}
        >
          <div>
            <ul className="form d-flex flex-wrap p-0 pt-2">
              {/* Vehicle */}
              {isTransfer && role === "vendor" && (
                <li className="col-12">
                  <label className="filedHd">{t("vehicle")}</label>
                  <select
                    className="formSelect"
                    name="vehicle_id"
                    value={formData?.vehicle_id}
                    onChange={handleChange}
                  >
                    <option value="">
                      {t("select")} {t("vehicle")}
                    </option>
                    {vehicles?.length > 0
                      ? vehicles.map((item, i) => (
                          <option value={item?.id} key={i}>
                            {item?.vehicle_type?.name}
                          </option>
                        ))
                      : ""}
                  </select>
                  {errors?.vehicle_id && (
                    <span style={{ color: "red" }}>{errors.vehicle_id}</span>
                  )}
                </li>
              )}

              {/* Route */}
              <li className="col-12">
                <label className="filedHd">{t("route")}</label>
                <select
                  className="formSelect"
                  name="route_id"
                  value={formData.route_id}
                  onChange={handleChange}
                >
                  <option value="">
                    {t("select")} {t("route")}
                  </option>
                  {routes?.length > 0
                    ? routes?.map((item, i) => (
                        <option value={item?.id} key={i}>
                          {item?.route_name}
                        </option>
                      ))
                    : ""}
                </select>
                {errors?.route_id && (
                  <span style={{ color: "red" }}>{errors.route_id}</span>
                )}
              </li>

              {/* Driver */}
              <li className="col-12">
                <label className="filedHd">{t("driver")}</label>
                <select
                  className="formSelect"
                  name="driver_id"
                  value={formData.driver_id}
                  onChange={handleChange}
                >
                  <option value="">
                    {t("select")} {t("driver")}
                  </option>
                  {drivers?.length > 0
                    ? drivers?.map((item, i) => (
                        <option value={item?.driver_details?.driver_id} key={i}>
                          {item?.full_name}
                        </option>
                      ))
                    : ""}
                </select>
                {errors?.driver_id && (
                  <span style={{ color: "red" }}>{errors.driver_id}</span>
                )}
              </li>

              {/* Driver Schedules Section */}
              {formData.driver_id && (
                <li className="col-12">
                  <label className="filedHd">
                    {t("available")} {t("schedules")} ({t("status")}:{" "}
                    {t("available")})
                  </label>

                  {/* Show message if no available schedules */}
                  {availableSchedules.length === 0 ? (
                    <div className="alert alert-warning p-2 mb-2 d-flex justify-content-between align-items-center">
                      <small>
                        <i className="fas fa-exclamation-triangle me-2"></i>
                        {t("no-schedules-available-for-this-driver")}.
                      </small>
                      <Link
                        to={`/drivers/${formData.driver_id}/schedules`}
                        className="badge bg-secondary py-2"
                      >
                        {t("add")} {t("schedule")}
                      </Link>
                    </div>
                  ) : (
                    <div className="schedules-list mb-3">
                      <select
                        className="formSelect"
                        name="driver_schedule_id"
                        value={formData.driver_schedule_id}
                        onChange={handleChange}
                      >
                        <option value="">
                          {t("driver")} {t("schedule")}
                        </option>
                        {availableSchedules.map((schedule, i) => (
                          <option value={schedule.id} key={i}>
                            {formatScheduleOption(schedule)}
                          </option>
                        ))}
                      </select>
                      {errors?.driver_schedule_id && (
                        <span style={{ color: "red" }}>
                          {errors.driver_schedule_id}
                        </span>
                      )}
                    </div>
                  )}

                  {/* Display all schedules for reference */}
                  {driverSchedule.length > 0 && (
                    <div className="all-schedules mt-3">
                      <h6 className="filedHd mb-2">
                        <i className="fas fa-calendar-alt me-2"></i>
                        {t("all")} {t("driver")} {t("schedules")}
                      </h6>
                      <div className="table">
                        <table className="">
                          <thead>
                            <tr>
                              <th>{t("shift")}</th>
                              <th>{t("start")}</th>
                              <th>{t("end")}</th>
                              <th>{t("recurrence")}</th>
                              <th>{t("status")}</th>
                            </tr>
                          </thead>
                          <tbody>
                            {driverSchedule.map((schedule, i) => {
                              const statusInfo = getScheduleStatusInfo(
                                schedule?.status,
                              );

                              return (
                                <tr
                                  key={i}
                                  // className={
                                  //   schedule.status === "in_progress"
                                  //     ? "table-success"
                                  //     : "table-secondary"
                                  // }
                                >
                                  <td>
                                    <span
                                      className={`badge ${
                                        schedule.shift_type === "morning"
                                          ? "bg-primary"
                                          : schedule.shift_type === "afternoon"
                                            ? "bg-warning text-dark"
                                            : "bg-info"
                                      }`}
                                    >
                                      {capitalizeString(schedule.shift_type)}
                                    </span>
                                  </td>
                                  <td>
                                    <small>
                                      {formatDateToLocal(schedule.start_date)}{" "}
                                      {convertTime(schedule.start_time)}
                                    </small>
                                    {schedule.shift_type ===
                                      "morning_afternoon" &&
                                      schedule.return_start_time && (
                                        <div>
                                          <small className="text-muted">
                                            Aft:{" "}
                                            {convertTime(
                                              schedule.return_start_time,
                                            )}
                                          </small>
                                        </div>
                                      )}
                                  </td>
                                  <td>
                                    <small>
                                      {formatDateToLocal(schedule.end_date)}{" "}
                                      {convertTime(schedule.end_time)}
                                    </small>
                                    {schedule.shift_type ===
                                      "morning_afternoon" &&
                                      schedule.return_end_time && (
                                        <div>
                                          <small className="text-muted">
                                            Aft:{" "}
                                            {convertTime(
                                              schedule.return_end_time,
                                            )}
                                          </small>
                                        </div>
                                      )}
                                  </td>
                                  <td>
                                    <small>
                                      {capitalizeString(
                                        schedule.recurrence_type,
                                      )}
                                    </small>
                                    {schedule.recurrence_days &&
                                      schedule.recurrence_days !== "[]" && (
                                        <div>
                                          <small>
                                            {formatRecurrenceDays(
                                              schedule.recurrence_days,
                                            )}
                                          </small>
                                        </div>
                                      )}
                                  </td>
                                  <td>
                                    <span
                                      className={statusInfo.className}
                                      // style={{
                                      //   cursor: isUpdateLoading
                                      //     ? "not-allowed"
                                      //     : "pointer",
                                      //   opacity: isUpdateLoading ? 0.7 : 1,
                                      //   pointerEvents: isUpdateLoading
                                      //     ? "none"
                                      //     : "auto",
                                      // }}
                                    >
                                      {statusInfo.label}
                                    </span>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                      <div className="alert alert-info p-2 mb-0">
                        <small>
                          <i className="fas fa-info-circle me-2"></i>
                          {t("only-schedules-with")}{" "}
                          <strong>{t("available")}</strong>{" "}
                          {t("status-can-be-selected. schedules-with")}{" "}
                          <strong>{t("scheduled")}</strong>{" "}
                          {t("status-are-not-available-for-assignment")}.
                        </small>
                      </div>
                    </div>
                  )}
                </li>
              )}
            </ul>
            <div className="text-center mt-5">
              <Button
                disabled={isLoading}
                className="smallBtn cancel me-3"
                onClick={handleSubmit}
              >
                {isLoading ? (
                  <div className="spinner-border" role="status">
                    <span className="sr-only p-0"></span>
                  </div>
                ) : (
                  `${t("assign")}`
                )}
              </Button>
            </div>
          </div>
        </Modal.Body>
      </Modal>

      {/* <style>
        {`
         
            .status {
          padding: 5px 12px;
          border-radius: 20px;
          font-size: 12px;
          font-weight: 500;
          display: inline-block;
          width: 100px;
          text-align: center;
        }
        .status.scheduled {
          background-color: #e7f1ff;
          color: #004085;
          border: 1px solid #b8daff;
        }
        .status.available {
          background-color: #d1f2eb;
          color: #0c5460;
          border: 1px solid #bee5eb;
        }
        .status.completed {
          background-color: #d4edda;
          color: #155724;
          border: 1px solid #c3e6cb;
        }
        .status.cancelled {
          background-color: #f8d7da;
          color: #721c24;
          border: 1px solid #f5c6cb;
        }
        `}
      </style> */}
    </>
  );
};
export default BusingAssignDriver;
